// Web replacement for react-native-keychain.
// TODO(pwa): this is plain localStorage — no hardware-backed protection. Before release, tokens must
// move out of persistent storage and biometrics must go through WebAuthn (see PWA_MIGRATION_PLAN.md).
const PREFIX = 'keychain:';

type Options = { service?: string; [key: string]: unknown };
type Credentials = { username: string; password: string; service: string; storage: string };

export enum BIOMETRY_TYPE {
  TOUCH_ID = 'TouchID',
  FACE_ID = 'FaceID',
  OPTIC_ID = 'OpticID',
  FINGERPRINT = 'Fingerprint',
  FACE = 'Face',
  IRIS = 'Iris',
}

export enum ACCESS_CONTROL {
  USER_PRESENCE = 'UserPresence',
  BIOMETRY_ANY = 'BiometryAny',
  BIOMETRY_CURRENT_SET = 'BiometryCurrentSet',
  DEVICE_PASSCODE = 'DevicePasscode',
  APPLICATION_PASSWORD = 'ApplicationPassword',
  BIOMETRY_ANY_OR_DEVICE_PASSCODE = 'BiometryAnyOrDevicePasscode',
  BIOMETRY_CURRENT_SET_OR_DEVICE_PASSCODE = 'BiometryCurrentSetOrDevicePasscode',
}

export enum ACCESSIBLE {
  WHEN_UNLOCKED = 'AccessibleWhenUnlocked',
  AFTER_FIRST_UNLOCK = 'AccessibleAfterFirstUnlock',
  WHEN_PASSCODE_SET_THIS_DEVICE_ONLY = 'AccessibleWhenPasscodeSetThisDeviceOnly',
  WHEN_UNLOCKED_THIS_DEVICE_ONLY = 'AccessibleWhenUnlockedThisDeviceOnly',
  AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY = 'AccessibleAfterFirstUnlockThisDeviceOnly',
}

export enum AUTHENTICATION_TYPE {
  DEVICE_PASSCODE_OR_BIOMETRICS = 'AuthenticationWithBiometricsDevicePasscode',
  BIOMETRICS = 'AuthenticationWithBiometrics',
}

export enum SECURITY_LEVEL {
  SECURE_SOFTWARE = 'SECURE_SOFTWARE',
  SECURE_HARDWARE = 'SECURE_HARDWARE',
  ANY = 'ANY',
}

const key = (options?: Options) => PREFIX + (options?.service ?? 'default');

export async function setGenericPassword(username: string, password: string, options?: Options) {
  localStorage.setItem(key(options), JSON.stringify({ username, password }));
  return { service: options?.service ?? 'default', storage: 'localStorage' };
}

export async function getGenericPassword(options?: Options): Promise<Credentials | false> {
  const raw = localStorage.getItem(key(options));
  if (!raw) return false;
  try {
    const { username, password } = JSON.parse(raw);
    return { username, password, service: options?.service ?? 'default', storage: 'localStorage' };
  } catch {
    return false;
  }
}

export async function resetGenericPassword(options?: Options) {
  localStorage.removeItem(key(options));
  return true;
}

export async function hasGenericPassword(options?: Options) {
  return localStorage.getItem(key(options)) !== null;
}

// No biometrics in the browser until WebAuthn support lands.
export async function getSupportedBiometryType(): Promise<BIOMETRY_TYPE | null> {
  return null;
}

export async function canImplyAuthentication() {
  return false;
}

export async function getSecurityLevel() {
  return SECURITY_LEVEL.SECURE_SOFTWARE;
}
